/**
 * goToChannel action.
 */
import AnalyticsInstance from '@shared/utils/analytics';
import Channel from '@shared/classes/channel';
import Event from '@shared/classes/event';
import Modals from '@shared/types/enums/modals';
import Pages from '@pages';
import ParentalUtils from '@shared/utils/parental';
import { OmnitrackPlaybackInteraction } from '@tvup/omnitrack-js';
import { clearPauseAd } from '@shared/store/slices/ads.slice';
import { popRoute, pushRoute } from '@shared/store/thunks/route.thunk';
import { setChannel } from '@shared/store/thunks/channels.thunk';
import { setModal, setModalParams } from '@shared/store/slices/modal.slice';
import { store } from '@shared/store/store';
import { updateParams } from '@shared/store/slices/router.slice';

/**
 * Props interface for goToChannel action.
 */
interface Props {
  channel?: Channel | number;
  checkParental?: boolean;
  deadman?: boolean;
  pop?: boolean;
  restart?: boolean;
}

/**
 * Access channel function. Sets the new channel and updates the router.
 */
function accessChannel(props: Props) {
  // Set new channel
  const { channel, deadman, pop, restart } = props;
  if (channel) {
    store.dispatch(clearPauseAd());
    store.dispatch(setChannel(channel));
    const { router } = store.getState();
    // Only keep one player at the stack at most.
    // Deadman always overwrites the route so the stack is emptied and
    // coming back from the zapper lands on the initial page.
    if (!deadman && (pop || router.route.stack.includes(Pages.ZAPPER))) {
      store.dispatch(popRoute());
      store.dispatch(
        updateParams({
          content: channel,
          ...(restart ? { restart, position: 0 } : null),
        })
      );
    } else {
      setTimeout(() => {
        // Without this setTimeout, currentChannel doesn't change in time for Kepler
        store.dispatch(
          pushRoute({
            route: { Page: Pages.ZAPPER, overwrite: deadman },
            params: {
              current: {
                content: channel,
                ...(deadman ? { deadman } : null),
                ...(restart ? { restart, position: 0 } : null),
              },
              stack: {
                channel: channel,
                stacked: !deadman,
              },
              push: true,
            },
          })
        );
      });
    }
  }
}

/**
 * Checks if the channel is valid and accessible, and if so, accesses it.
 */
export default function goToChannel(props: Props) {
  const { channel, checkParental, deadman } = props;
  store.dispatch(clearPauseAd());
  if (deadman) {
    AnalyticsInstance.setPlaybackInteraction(
      OmnitrackPlaybackInteraction.AUTOMATIC
    );
  } else {
    AnalyticsInstance.setPlaybackInteraction(
      OmnitrackPlaybackInteraction.CLICK
    );
  }
  if (
    !(
      checkParental &&
      typeof channel !== 'number' &&
      ParentalUtils.checkParentalControl(channel?.currentEvent as Event, () => {
        store.dispatch(
          setModalParams({
            callback: () => {
              accessChannel(props);
            },
          })
        );
        store.dispatch(setModal(Modals.PARENTAL_PIN_GENERIC));
      })
    )
  ) {
    accessChannel(props);
  }
}
